import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ID, PredefinedLists } from 'src/models';
import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InlineInput } from 'src/components/Inputs';
import { CONFIG } from 'src/config';
import { getPredefinedList } from 'src/store/predefinedLists/predefinedLists.getters';
import { t } from 'src/tools/text.tools';
import { ifTrue } from 'src/tools/logic.tools';

const EMPTY_ARRAY: ID[] = [];

const TXT = t(['dropdown']);

export function CategoriesDropdown() {
  const {
    value = EMPTY_ARRAY,
    onChange,
    classNames,
    onDiscard = mockFn,
    isDisabled,
    isReadonly,
    isAutoFocus,
    onClick,
    customization = {},
  } = useContext(InputContext);

  const { dropdownMinWidth = 'container-width' } = customization;

  const [isOpen, setIsOpen] = useState(false);

  const categoriesItems = useSelector(getPredefinedList)(PredefinedLists.CHANGE_ORDER_CATEGORIES);

  const isItemsNotAvailable = !isReadonly && !categoriesItems?.length;

  const items = useMemo(
    () => categoriesItems?.map(i => ({ ...i, isChecked: value.includes(i.id) })),
    [value, categoriesItems],
  );

  function handleOnItemClick(id: ID) {
    const newValue = value.includes(id) ? value.filter((i: ID) => i !== id) : [...value, id];

    onChange(newValue, true);
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) onDiscard();
  }

  function handleOnFocus(callback: () => void) {
    return function call(e: React.FocusEvent) {
      e.preventDefault();

      callback();
    };
  }

  function renderBaseComponent({ onClick, onFocus, onBlur }: BaseComponentProps) {
    return (
      <InlineInput
        className={classNames?.input}
        value={value.join(CONFIG.categoriesSeparator)}
        onFocus={handleOnFocus(onFocus)}
        onClick={getHandleOnClick(onClick)}
        onBlur={onBlur}
        isDisabled={isDisabled || !categoriesItems?.length}
        autoFocus={isAutoFocus}
        onDiscard={onDiscard}
        onSubmit={onDiscard}
        isReadonly={isReadonly}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={items}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      dropdownMinWidth={dropdownMinWidth}
      isOpen={isOpen}
      onRequestOpenClose={handleOnRequestOpenClose}
      tooltip={ifTrue(isItemsNotAvailable, TXT('dropdownValuesIsNotAvailable'))}
    />
  );
}
